import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { DEFAULT_PAGE_LIMIT, type ClauseSearchJudgement, type SearchJudgementListRequest, type SearchJudgementListResponse } from '@clm/shared-types';

import { fetchJudgements, retractJudgement } from '../../api/search-quality';
import { CursorPager } from '../../components/CursorPager';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { ErrorNotice } from '../../components/ErrorNotice';
import { EMPTY_CURSOR_STACK, pageNumber, popCursor, pushCursor, type CursorStack } from '../../components/cursor-stack';
import { panelProblemAction, type PanelProblemState } from './panel-action';
import { toProblem, type Panel } from './panel-state';

/**
 * Judgements — the ledger of recorded search judgements, with Retract — FR-26,
 * NFR-13 · CLM-39.
 *
 * One `.card`, one `Panel<T>`, its own loading line, its own `ErrorNotice`, its
 * own tiles; it never fuses a number with another panel's. Mounted by
 * `SearchQuality.tsx` (CLM-40's host) at the CLM-39 slot.
 *
 * "Stale only" is a **filter, default off** — the ledger lists everything; the
 * re-confirmation queue is CLM-40's Stale judgements panel directly above this
 * one. A stale row shows `v{judged} → v{current}` and the 0.3 weight in the bad
 * tone, with the hint that re-confirmation is a fresh judgement from Search
 * (this panel has no vector to send). Retract is confirmed inline and then
 * re-fetches — never patches — the list.
 */

function formatDate(iso: string): string {
  return iso.slice(0, 10);
}

const CLAUSE_CODE = /^[A-Z0-9][A-Z0-9-]{1,31}$/;

export function JudgementLedgerPanel() {
  const [staleOnly, setStaleOnly] = useState(false);
  const [clauseCode, setClauseCode] = useState('');
  const [stack, setStack] = useState<CursorStack>(EMPTY_CURSOR_STACK);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [panel, setPanel] = useState<Panel<SearchJudgementListResponse>>({ kind: 'loading' });
  const [busy, setBusy] = useState(true);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [retract, setRetract] = useState<{ readonly kind: 'idle' } | { readonly kind: 'busy' } | { readonly kind: 'done'; readonly audit_id: string } | PanelProblemState>({ kind: 'idle' });
  const abortRef = useRef<AbortController | null>(null);

  const trimmedCode = clauseCode.trim().toUpperCase();
  const codeFilter = CLAUSE_CODE.test(trimmedCode) ? trimmedCode : undefined;

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const request: SearchJudgementListRequest = {
      /* Sent explicitly, every time: the API defaults it off too, but the request says what it wants. */
      stale_only: staleOnly,
      ...(codeFilter === undefined ? {} : { clause_code: codeFilter }),
      page: cursor === undefined ? { limit: DEFAULT_PAGE_LIMIT } : { limit: DEFAULT_PAGE_LIMIT, cursor },
    };
    setBusy(true);

    void (async () => {
      try {
        const response = await fetchJudgements(request, controller.signal);
        if (controller.signal.aborted) return;
        setPanel({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPanel(toProblem(error));
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [staleOnly, codeFilter, cursor, reloadNonce]);

  function resetPaging() {
    setStack(EMPTY_CURSOR_STACK);
    setCursor(undefined);
  }

  function reload() {
    setReloadNonce((n) => n + 1);
  }

  function doRetract(item: ClauseSearchJudgement) {
    setRetract({ kind: 'busy' });
    void (async () => {
      try {
        const response = await retractJudgement(item.feedback_id);
        setRetract({ kind: 'done', audit_id: response.audit.audit_id });
      } catch (error) {
        setRetract(toProblem(error));
      } finally {
        setConfirming(null);
        /* Re-fetch, never patch: the view decides what is still live. */
        reload();
      }
    })();
  }

  const columns: readonly DataTableColumn<ClauseSearchJudgement>[] = [
    { id: 'query', header: 'Query', cell: (item) => item.query_text },
    {
      id: 'clause',
      header: 'Clause',
      cell: (item) => (
        <Link to={`/clauses/${encodeURIComponent(item.clause_code)}`}>
          <code>{item.clause_code}</code>
        </Link>
      ),
    },
    { id: 'judgement', header: 'Judgement', cell: (item) => item.judgement },
    {
      id: 'against',
      header: 'Judged against',
      cell: (item) =>
        item.is_stale ? (
          <>
            v{item.clause_version_no} → v{item.current_version_no ?? '—'}
            <br />
            <span className="mut">Re-confirm by judging the current version from Search</span>
          </>
        ) : (
          `v${String(item.clause_version_no)}`
        ),
    },
    {
      id: 'weight',
      header: 'Weight',
      cell: (item) => (
        <span className="sq-weight" data-tone={item.staleness_weight < 1 ? 'bad' : 'plain'}>
          {item.staleness_weight.toFixed(1)}
        </span>
      ),
    },
    { id: 'by', header: 'By', cell: (item) => item.judged_by_upn },
    { id: 'when', header: 'When', cell: (item) => formatDate(item.judged_utc) },
  ];

  const response = panel.kind === 'ready' ? panel.response : null;
  const items = response?.items ?? [];

  return (
    <section className="card" aria-labelledby="sq-judgements">
      <h3 id="sq-judgements">Judgements</h3>
      <p>
        Every explicit relevance judgement still in force, stale first. A judgement recorded against a superseded clause version
        weighs 0.3 until it is re-confirmed from Search or retracted here. Retract and re-confirm each write one audit row.
      </p>

      <div className="sq-filters">
        <label>
          <input
            type="checkbox"
            checked={staleOnly}
            onChange={(event) => {
              setStaleOnly(event.target.checked);
              resetPaging();
            }}
          />
          Stale only
        </label>
        <label>
          Clause code
          <input
            type="text"
            value={clauseCode}
            maxLength={32}
            onChange={(event) => {
              setClauseCode(event.target.value);
              resetPaging();
            }}
          />
        </label>
      </div>

      {retract.kind === 'done' ? (
        <p role="status" className="mut">
          Judgement retracted · audit <code>{retract.audit_id}</code>
        </p>
      ) : null}
      {retract.kind === 'problem' ? (
        <ErrorNotice
          kind={retract.problem}
          detail={retract.detail}
          correlation_id={retract.correlation_id}
          action={
            retract.problem === 'forbidden' ? (
              <p className="mut">
                Retracting is a CEQ-Legal action (<code>legal_reviewer</code>).
              </p>
            ) : undefined
          }
        />
      ) : null}

      {panel.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading recorded judgements…
        </p>
      ) : null}

      {panel.kind === 'problem' ? (
        <ErrorNotice
          kind={panel.problem}
          detail={panel.detail}
          correlation_id={panel.correlation_id}
          action={panelProblemAction(panel.problem, 'search-judgements (GET /v1/search/judgements)', reload)}
        />
      ) : null}

      {response ? (
        <>
          <div className="totals">
            <div className="total">
              <span>Active</span>
              <b>{response.totals.active}</b>
            </div>
            <div className="total" data-tone={response.totals.stale > 0 ? 'bad' : 'plain'}>
              <span>Stale</span>
              <b>{response.totals.stale}</b>
            </div>
          </div>

          {confirming !== null ? (
            <p role="status" className="sq-retract">
              Retract judgement <code>{confirming}</code>? This is recorded with your name.
              <button
                type="button"
                className="btn sm"
                disabled={retract.kind === 'busy'}
                onClick={() => {
                  const item = items.find((candidate) => candidate.feedback_id === confirming);
                  if (item !== undefined) doRetract(item);
                }}
              >
                Confirm retract
              </button>
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  setConfirming(null);
                }}
              >
                Keep
              </button>
            </p>
          ) : null}

          <DataTable
            caption="Recorded search judgements, stale first"
            columns={columns}
            rows={items}
            rowKey={(item) => item.feedback_id}
            isCurrent={(item) => item.feedback_id === confirming}
            busy={busy}
            activate={{
              header: 'Retract',
              label: (item) => `Retract judgement ${item.feedback_id}`,
              buttonId: (item) => `sq-retract-${item.feedback_id}`,
              onActivate: (item) => {
                setRetract({ kind: 'idle' });
                setConfirming(item.feedback_id);
              },
            }}
            empty={
              staleOnly ? (
                <>
                  <p>
                    <strong>Nothing to re-confirm.</strong>
                  </p>
                  <p>No judgement was recorded against a superseded clause version.</p>
                </>
              ) : (
                <>
                  <p>
                    <strong>No judgements recorded.</strong>
                  </p>
                  <p>Judge a result on the Search surface.</p>
                </>
              )
            }
          />

          {items.length > 0 ? (
            <CursorPager
              next_cursor={response.page.next_cursor}
              historyDepth={stack.cursors.length}
              busy={busy}
              pageNumber={pageNumber(stack)}
              returnedCount={response.page.returned_count}
              totalInScope={response.totals.active}
              onNext={() => {
                const next = response.page.next_cursor;
                if (next === null) return;
                setStack((current) => pushCursor(current, next));
                setCursor(next);
              }}
              onPrevious={() => {
                const popped = popCursor(stack);
                setStack(popped.stack);
                setCursor(popped.cursor);
              }}
            />
          ) : null}
        </>
      ) : null}
    </section>
  );
}
